/**
 * OnlinePlayersList Component
 *
 * Displays a list of players with their activity status.
 * Includes search, pagination, and real-time updates.
 * Can optionally show offline players.
 */

import { useState, useMemo, useEffect, useCallback } from 'react';
import { usePresenceSocket } from '../hooks/usePresenceSocket';
import { useAuth } from '../contexts/AuthContext';
import type { OnlinePlayer, ActivityStatus } from '@drawn-of-war/shared';
import styles from './OnlinePlayersList.module.css';

/**
 * Activity status display configuration
 * Uses colored dots + text labels for design system alignment
 */
const ACTIVITY_DISPLAY: Record<ActivityStatus | 'offline', { label: string; dotClass: string }> = {
  browsing: { label: 'Online', dotClass: 'dotOnline' },
  in_lobby: { label: 'In Lobby', dotClass: 'dotLobby' },
  deploying: { label: 'Deploying', dotClass: 'dotDeploying' },
  in_battle: { label: 'In Battle', dotClass: 'dotBattle' },
  creating: { label: 'Creating', dotClass: 'dotCreating' },
  offline: { label: 'Offline', dotClass: 'dotOffline' }
};

/**
 * Extended player type that includes offline status
 */
interface PlayerWithStatus extends OnlinePlayer {
  isOnline: boolean;
}

/**
 * Props for OnlinePlayersList
 */
interface OnlinePlayersListProps {
  /** Callback when a player is clicked */
  onPlayerClick?: (player: OnlinePlayer) => void;
  /** Maximum players to show initially */
  initialLimit?: number;
  /** Show search input */
  showSearch?: boolean;
  /** Compact mode (less details) */
  compact?: boolean;
  /** Show offline players too */
  showOffline?: boolean;
}

/**
 * Single player row component
 */
function PlayerRow({
  player,
  onClick,
  compact,
  isOnline
}: {
  player: OnlinePlayer;
  onClick?: () => void;
  compact?: boolean;
  isOnline?: boolean;
}) {
  const status = isOnline === false ? 'offline' : player.activity;
  const activity = ACTIVITY_DISPLAY[status];

  return (
    <li
      className={`${styles.playerRow} ${onClick ? styles.clickable : ''} ${compact ? styles.compact : ''} ${!isOnline ? styles.offline : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
    >
      <span className={`${styles.statusDot} ${styles[activity.dotClass]}`} title={activity.label} />
      <span className={styles.displayName}>{player.displayName}</span>
      {!compact && (
        <>
          <span className={styles.activityLabel}>{activity.label}</span>
          <span className={styles.moreButton} title="View profile">
            ···
          </span>
        </>
      )}
    </li>
  );
}

/**
 * OnlinePlayersList Component
 */
export function OnlinePlayersList({
  onPlayerClick,
  initialLimit = 20,
  showSearch = true,
  compact = false,
  showOffline = false
}: OnlinePlayersListProps) {
  const { getIdToken, user } = useAuth();
  const { players: onlinePlayers, isConnected, error: socketError, totalOnline } = usePresenceSocket();
  const [allUsers, setAllUsers] = useState<PlayerWithStatus[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [displayLimit, setDisplayLimit] = useState(initialLimit);

  // Fetch all users when showOffline is true
  const fetchAllUsers = useCallback(async () => {
    if (!showOffline) return;

    setLoadingUsers(true);
    setFetchError(null);

    try {
      const token = await getIdToken();
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

      const response = await fetch(`${apiUrl}/api/users/all?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setAllUsers(data.users.map((u: any) => ({
        id: u.id,
        displayName: u.displayName,
        activity: u.presence?.activity || 'browsing',
        stats: u.stats || { wins: 0, losses: 0 },
        isOnline: u.presence?.status === 'online'
      })));
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  }, [showOffline, getIdToken]);

  // Fetch all users on mount if showOffline
  useEffect(() => {
    if (showOffline) {
      fetchAllUsers();
    }
  }, [showOffline, fetchAllUsers]);

  // Build combined player list
  const players = useMemo(() => {
    if (!showOffline) {
      // Just use online players from socket
      return onlinePlayers.map(p => ({ ...p, isOnline: true }));
    }

    // Merge all users with online status from socket
    const onlineIds = new Set(onlinePlayers.map(p => p.id));
    return allUsers.map(u => ({
      ...u,
      isOnline: onlineIds.has(u.id),
      // Update activity from real-time data if online
      activity: onlineIds.has(u.id)
        ? (onlinePlayers.find(p => p.id === u.id)?.activity || u.activity)
        : u.activity
    })).filter(p => p.id !== user?.uid); // Exclude self
  }, [showOffline, onlinePlayers, allUsers, user?.uid]);

  // Filter players by search term
  const filteredPlayers = useMemo(() => {
    let result = players;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.displayName.toLowerCase().includes(term)
      );
    }

    // Sort: online first, then alphabetically
    return result.sort((a, b) => {
      if (a.isOnline && !b.isOnline) return -1;
      if (!a.isOnline && b.isOnline) return 1;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [players, searchTerm]);

  // Paginate results
  const displayedPlayers = useMemo(() => {
    return filteredPlayers.slice(0, displayLimit);
  }, [filteredPlayers, displayLimit]);

  // How many more can be shown
  const remainingCount = filteredPlayers.length - displayedPlayers.length;

  // Handle show more
  const handleShowMore = () => {
    setDisplayLimit(prev => prev + initialLimit);
  };

  const error = socketError || fetchError;

  // Render error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          Failed to load players: {error}
        </div>
      </div>
    );
  }

  const title = showOffline ? 'Players' : 'Online Players';
  const count = showOffline ? `${totalOnline} online` : totalOnline;

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <h3 className={styles.title}>
          {title}
          <span className={styles.count}>({count})</span>
        </h3>
        {!isConnected && (
          <span className={styles.connectionStatus}>Connecting...</span>
        )}
      </header>

      {/* Search */}
      {showSearch && (
        <div className={styles.searchContainer}>
          <input
            type="search"
            placeholder="Search players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      )}

      {/* Loading state */}
      {loadingUsers && (
        <div className={styles.emptyState}>Loading players...</div>
      )}

      {/* Player list */}
      {!loadingUsers && displayedPlayers.length === 0 ? (
        <div className={styles.emptyState}>
          {searchTerm ? 'No players match your search' : 'No players found'}
        </div>
      ) : !loadingUsers && (
        <ul className={styles.playerList}>
          {displayedPlayers.map(player => (
            <PlayerRow
              key={player.id}
              player={player}
              onClick={onPlayerClick ? () => onPlayerClick(player) : undefined}
              compact={compact}
              isOnline={player.isOnline}
            />
          ))}
        </ul>
      )}

      {/* Show more button */}
      {remainingCount > 0 && (
        <button
          className={styles.showMoreButton}
          onClick={handleShowMore}
        >
          Show More ({remainingCount} more)
        </button>
      )}
    </div>
  );
}
